<template>
  <ActionPopoverForm
    v-loading="isLoadingCampaigns"
    :is-loading="isAddingKeyword"
    :show="show"
    :title="$t('ADVERTISEMENT.ADD_TO_KEYWORDS')"
    class="keyword-planner-add-keyword-form"
    @hide="closeAndReset"
    @back="resetAndBack"
    @save="validateAndAddKeyword"
  >
    <ElForm
      ref="addKeywordForm"
      label-position="left"
      label-width="150px"
      :model="formModel"
      :rules="bidRules"
    >
      <ElFormItem
        prop="campaign"
        :label="$t('ADVERTISEMENT.KEYWORDPLANNER.ADD_TO_CAMPAIGN')"
      >
        <ElSelect v-model="formModel.campaign">
          <ElOption
            v-for="campaign in campaigns"
            :key="campaign.id"
            :label="campaign.name"
            :value="campaign.id"
          />
        </ElSelect>
      </ElFormItem>
      <ElFormItem
        prop="matchType"
        :label="$t('ADVERTISEMENT.KEYWORDPLANNER.CHOOSE_MATCH_TYPE')"
      >
        <ElSelect v-model="formModel.matchType">
          <ElOption
            v-for="matchType in matchingTypes"
            :key="matchType.property"
            :label="matchType.label"
            :value="matchType.property"
          />
        </ElSelect>
      </ElFormItem>
      <ElFormItem
        prop="price"
        :label="$t('ADVERTISEMENT.KEYWORDPLANNER.KEYWORD_PRICE')"
      >
        <ElInput
          v-model.number="formModel.price"
          class="el-input-hide-numeric-counter"
          type="number"
        >
          <template #append>
            {{ $t('RIALS') }}
          </template>
        </ElInput>
      </ElFormItem>
    </ElForm>
  </ActionPopoverForm>
</template>

<script>
import { SearchAdsCampaigns, KeywordPlanner } from '@/api/api';
import { AD_KEYWORD_PLANNER_ADD_TO_KEYWORD_MODAL_BUTTON } from '@/constants/gtm-events';
import ActionPopoverForm from '../../../../../common/ActionPopoverForm';
import { matchingTypes } from '../../../../../common/constants';
import form from '@/mixins/form';
import required from '@/utils/validation-rules/required';
import extractQueryTransformer from '../common/extractQueryTransformer';

const FORM_REF_NAME = 'addKeywordForm';

export default {
  name: 'KeywordPlannerAddKeywordForm',
  components: {
    ActionPopoverForm,
  },
  mixins: [form],
  inject: ['$selectedSuggestedKeywords'],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      formModel: {
        campaign: '',
        matchType: '',
        price: 0,
      },
      campaigns: [],
      isAddingKeyword: false,
      isLoadingCampaigns: true,
    };
  },
  computed: {
    matchingTypes() {
      return matchingTypes;
    },
    bidRules() {
      const requiredRule = {
        ...required(this.$t('VALIDATION_REQUIRED_ERR')),
        trigger: ['blur', 'change'],
      };
      return {
        campaign: requiredRule,
        matchType: requiredRule,
        price: requiredRule,
      };
    },
  },
  mounted() {
    this.getCampaigns();
  },
  methods: {
    closeAndReset() {
      this.reset().then(() => {
        this.close();
      });
    },
    resetAndError(errors) {
      this.reset().then(() => {
        this.$emit('error', errors);
      });
    },
    reset() {
      return this.resetForm(FORM_REF_NAME);
    },
    resetAndBack() {
      this.reset().then(() => {
        this.$emit('back');
      });
    },
    validateAndAddKeyword() {
      if (!this.isValid(FORM_REF_NAME)) return false;

      return this.addKeyword();
    },
    addKeyword() {
      this.$gtm.trackEvent(AD_KEYWORD_PLANNER_ADD_TO_KEYWORD_MODAL_BUTTON);

      this.isAddingKeyword = true;
      return KeywordPlanner.addBulkBids({
        campaignId: this.formModel.campaign,
        queries: extractQueryTransformer(this.$selectedSuggestedKeywords()),
        matchType: this.formModel.matchType,
        price: this.formModel.price,
      })
        .then(({ message, errors }) => {
          if (!errors.length) {
            this.$message.success(message);
            this.closeAndReset();
            return;
          }

          this.resetAndError(errors);
        })
        .catch(({ message }) => {
          this.$message.error(message);
        })
        .finally(() => {
          this.isAddingKeyword = false;
        });
    },
    getCampaigns() {
      this.isLoadingCampaigns = true;
      return SearchAdsCampaigns.getCampaigns()
        .then(campaigns => {
          this.campaigns = campaigns;
        })
        .catch(({ message }) => {
          if (message) {
            this.$message.error(message);
          }
        })
        .finally(() => {
          this.isLoadingCampaigns = false;
        });
    },
    close() {
      this.$emit('hide');
    },
  },
};
</script>

<style lang="scss">
.keyword-planner-add-keyword-form {
  color: $lb-black;

  .el-form-item__content {
    @include rtl() {
      margin-left: 0 !important;
      margin-right: 150px;
    }
  }
}
</style>
